import React, { Component } from 'react';
import {Button, Col, Form, Row} from 'react-bootstrap';
import '../App.css';
import { embedComponent } from '../embed';
import { minMaxValidator } from '../utils/validators/index';
import formatCurrency from '../utils/formatters/formatCurrency';
import formatPercentage from '../utils/formatters/formatPercentage';
import Calculator from '../components/Calculator/index';
import CalculatorGroup from '../components/CalculatorGroup';
import CalculatorResults from '../components/CalculatorResults/index';
import CalculatorResult from '../components/CalculatorResult/index';
import CurrencyField from '../components/CurrencyField';
import InformationBlock from '../components/InformationBlock';
import PercentageField from '../components/PercentageField/index';
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators';
import SimpleCalculatorResult from '../components/SimpleCalculatorResult';
import SimpleSliderField from '../components/SimpleSliderField';

const popoverMessages = {
  grossMonthlyIncome: {
    title: 'Gross monthly income',
    body: [
        'How much you make each month before taxes'
    ]
  },
  estimatedHousingBudget: {
    title: 'Estimated housing budget',
    body: [
      'Your Estimated Housing Budget is 28% of your Gross Monthly Income (above).  This is the maximum amount you should spend on total housing according to most lenders.'
    ]
  },
  adjustedMonthlyHousingBudget: {
    title: 'Adjusted monthly housing budget',
    body: [
      'If your Monthly debt payments + your estimated housing budget add up to more than 36%, then your housing budget is adjusted downwards to meet this limit.'
    ]
  },
  ongoingHousingExpenses: {
    title: 'Ongoing housing expenses',
    body: [
      'These are monthly expenses beyond your mortgage payment and monthly debt payments you must take into account.'
    ]
  },
  simpleAnnualHouseholdIncome: {
    title: 'Annual household income',
    body: [
      'Enter your total gross annual income － that’s the amount of money you make before taxes. If you\'re buying with someone else, enter their income, too.'
    ]
  },
  simpleMonthlyDebtPayments: {
    title: 'Monthly debt payments',
    body: [
      'Recurring payments like car loans and credit card payments, but not everyday expenses like utilities or groceries.'
    ]
  },
  simpleDownPayment: {
    title: 'Down payment',
    body: [
      'The amount of cash you can put down at closing.'
    ]
  },
  interestRate: {
    title: 'Interest Rate',
    body: [
        'We’ve defaulted interest to 4.125%, a common average interest as set by the Fed, however you can change this number if you have a different interest rate in mind.'
    ]
  }
};

class AffordHomeBudgetCalculator extends Component {
  constructor (props) {
    super(props);

    this.state = AffordHomeBudgetCalculator.getInitialState();

    this.clearState = this.clearState.bind(this);
    this.onValidation = this.onValidation.bind(this);
    this.getCalculatorValues = this.getCalculatorValues.bind(this);
    this.setCalculatorValues = this.setCalculatorValues.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
    this.toggleCalculator = this.toggleCalculator.bind(this);
  }

  static getInitialState() {
    return {
      detailedView: false,
      grossMonthlyIncome: 0,
      creditCardDebt: 0,
      studentDebt: 0,
      autoLoan: 0,
      otherDebt: 0,
      propertyTax: 0,
      insurance: 0,
      maintenance: 0,
      interestRate: 4.125,
      downPayment: 0,
      simpleAnnualHouseholdIncome: 0,
      simpleMonthlyDebtPayments: 0,
      simpleDownPayment: 0,
      simpleDownPaymentPercent: 0,
      simpleInterestRate: 4.125,
      fieldErrors: {},
    }
  }

  componentDidMount() {
    document.addEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.addEventListener('getCalculatorValuesAffordHomeBudgetCalculator', this.getCalculatorValues, true);
    document.addEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  getCalculatorValues (event) {
    this.removeEventListeners();

    // Build newObj off state and exclude fieldErrors
    let newObj = {};
    Object.keys(AffordHomeBudgetCalculator.getInitialState()).forEach(key => {
      if (key !== 'fieldErrors' && key !== 'detailedView') {
          newObj[key] = this.state[key];
      }
    });

    const calculatedGrossMonthlyIncome = this.calculateGrossMonthlyIncome();
    newObj.calculatedGrossMonthlyIncome = calculatedGrossMonthlyIncome;
    newObj.calculatedEstimatedHousingBudget = this.calculateEstimatedHousingBudget(calculatedGrossMonthlyIncome);
    newObj.calculatedTotalMonthlyDebtPayments = this.calculateTotalMonthlyDebtPayments();
    newObj.calculatedAdjustedMonthlyHousingBudget = this.calculateAdjustedMonthlyHousingBudget(calculatedGrossMonthlyIncome);
    newObj.calculatedPropertyTax = this.calculatePropertyTax(calculatedGrossMonthlyIncome);
    newObj.calculatedInsurance = this.calculateInsurance(calculatedGrossMonthlyIncome);
    newObj.calculatedMaintenance = this.calculateMaintenance(calculatedGrossMonthlyIncome);
    newObj.calculatedTotalOngoingHousingExpenses = this.calculateTotalOngoingHousingExpenses(calculatedGrossMonthlyIncome);
    newObj.calculatedEstimatedMaxMortgagePayment = this.calculateEstimatedMaxMortgagePayment(calculatedGrossMonthlyIncome);
    newObj.calculatedEstimatedMaxMortgageAmount = this.calculateEstimatedMaxMortgageAmount(calculatedGrossMonthlyIncome);
    newObj.calculatedDownPayment = this.calculateDownPayment(calculatedGrossMonthlyIncome);
    newObj.calculatedMaxPrice = this.calculateMaxPrice(calculatedGrossMonthlyIncome);

    const cannotAffordDefault = this.calculateSimpleCannotAffordDefaultHouseBudget();
    newObj.calculatedSimpleHouseBudget = this.calculateSimpleHouseBudget(cannotAffordDefault);
    newObj.calculatedSimpleMonthlyPayments = this.calculateSimpleMonthlyPayment(cannotAffordDefault);
    newObj.calculatedSimpleDownPaymentPercent = this.calculateSimpleDownPaymentPercent(cannotAffordDefault);
    newObj.calculatedMinViableDownPaymentPercent = this.calculateSimpleMinViableDownPaymentPercent();

    document.dispatchEvent(new CustomEvent('receiveCalculatorStateAffordHomeBudgetCalculator', { detail: newObj}));
  }

  setCalculatorValues (event) {
    this.handleChange(event.detail.value, event.detail.name);
  }

  removeEventListeners () {
    document.removeEventListener('getCalculatorValuesAffordHomeBudgetCalculator', this.getCalculatorValues, true);
    document.removeEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.removeEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value })
  }

  clearState () {
    this.setState(AffordHomeBudgetCalculator.getInitialState())
  }

  toggleCalculator () {
    const { detailedView } = this.state;

    this.handleChange(!detailedView, 'detailedView');
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors)
        delete newErrors[name]
        this.setState({ fieldErrors: newErrors })
      }
    }
  }

  /**
   * Formula: Simple Annual Household Income / 12
   */
  calculateGrossMonthlyIncome () {
    const { simpleAnnualHouseholdIncome } = this.state;

    return Math.floor(simpleAnnualHouseholdIncome / 12);
  }

  /**
   * Formula: Gross Monthly Income * 28%
   */
  calculateEstimatedHousingBudget (calculatedGrossMonthlyIncome) {
    const { grossMonthlyIncome } = this.state;

    const monthlyIncome = (grossMonthlyIncome > 0) ? grossMonthlyIncome : calculatedGrossMonthlyIncome;

    return Math.ceil(monthlyIncome * 0.28);
  }

  /**
   * Formula: Sum Monthly Debt (Credit Card + Student + Auto + Other)
   */
  calculateTotalMonthlyDebtPayments () {
    const { creditCardDebt, studentDebt, autoLoan, otherDebt } = this.state;

    return parseInt(creditCardDebt, 10) + parseInt(studentDebt, 10) + parseInt(autoLoan, 10) + parseInt(otherDebt, 10);
  }

  /**
   * Formula: Ensure (Estimated Monthly Housing Budget + Total Monthly Debt Payments) <= 36% Gross Income
   */
  calculateAdjustedMonthlyHousingBudget (calculatedGrossMonthlyIncome) {
    const { grossMonthlyIncome } = this.state;

    const monthlyIncome = (grossMonthlyIncome > 0) ? grossMonthlyIncome : calculatedGrossMonthlyIncome;
    const threshold = monthlyIncome * 0.36;
    const cgmi = this.calculateGrossMonthlyIncome();
    const ehb = parseInt(this.calculateEstimatedHousingBudget(cgmi), 10);
    const mdp = parseInt(this.calculateTotalMonthlyDebtPayments(), 10);
    const currentPayments = ehb + mdp;

    // If Estimated Monthly Budget + Total Monthly Debt > 36% of Gross Income then decrease it
    if (currentPayments > threshold) {
      return threshold - mdp;
    } else {
      return ehb;
    }
  }

  /**
   * Formula: Adjusted Monthly Housing Budget * 15%
   */
  calculatePropertyTax (calculatedGrossMonthlyIncome) {
    const amhb = parseInt(this.calculateAdjustedMonthlyHousingBudget(calculatedGrossMonthlyIncome), 10);

    return Math.ceil(amhb * 0.15);
  }

  /**
   * Formula: Adjusted Monthly Housing Budget * 10%
   */
  calculateInsurance (calculatedGrossMonthlyIncome) {
    const amhb = parseInt(this.calculateAdjustedMonthlyHousingBudget(calculatedGrossMonthlyIncome), 10);

    return Math.ceil(amhb * 0.10);
  }

  /**
   * Formula: Adjusted Monthly Housing Budget * 5%
   */
  calculateMaintenance (calculatedGrossMonthlyIncome) {
    const amhb = parseInt(this.calculateAdjustedMonthlyHousingBudget(calculatedGrossMonthlyIncome), 10);

    return Math.ceil(amhb * 0.05);
  }

  /**
   * Formula: Sum Ongoing Housing Expenses (Property Tax + Insurance + Maintenance)
   */
  calculateTotalOngoingHousingExpenses (calculatedGrossMonthlyIncome) {
    const { propertyTax, insurance, maintenance } = this.state;

    const pt = propertyTax || this.calculatePropertyTax(calculatedGrossMonthlyIncome);
    const ins = insurance || this.calculateInsurance(calculatedGrossMonthlyIncome);
    const main = maintenance || this.calculateMaintenance(calculatedGrossMonthlyIncome);

    return parseInt(pt, 10) + parseInt(ins, 10) + parseInt(main, 10);
  }

  /**
   * Formula: Adjusted Monthly Housing Budget - Total Ongoing Housing Expenses
   */
  calculateEstimatedMaxMortgagePayment (calculatedGrossMonthlyIncome) {
    const amhb =  this.calculateAdjustedMonthlyHousingBudget(calculatedGrossMonthlyIncome);
    const ohe = this.calculateTotalOngoingHousingExpenses(calculatedGrossMonthlyIncome);

    return amhb - ohe;
  }

  /**
   * Formula: Estimated Maximum Mortgage Amount / [c * (1+c)^360]/[(1+c)^360 - 1]
   * c = Interest Rate / 12
   */
  calculateEstimatedMaxMortgageAmount (calculatedGrossMonthlyIncome) {
    const { interestRate } = this.state;

    const emmp = this.calculateEstimatedMaxMortgagePayment(calculatedGrossMonthlyIncome);
    const c = (parseFloat(interestRate) / 100) / 12;
    const cmod = Math.pow(1 + c, 360);
    const bottom = (c * cmod) / (cmod - 1);

    return Math.ceil(emmp / bottom);
  }

  /**
   * Formula: (Estimate Maximum Mortgage Amount / 0.80) * 0.20
   */
  calculateDownPayment (calculatedGrossMonthlyIncome) {
    const ema = this.calculateEstimatedMaxMortgageAmount(calculatedGrossMonthlyIncome);

    return Math.ceil((ema / 0.80) * 0.20);
  }

  /**
   * Formula: Estimated Maximumt Mortgage Amount + Down Payment
   */
  calculateMaxPrice (calculatedGrossMonthlyIncome) {
    const { downPayment } = this.state;

    const ema = this.calculateEstimatedMaxMortgageAmount(calculatedGrossMonthlyIncome);
    const dp = parseInt(downPayment, 10) || this.calculateDownPayment(calculatedGrossMonthlyIncome);

    return ema + dp;
  }

  calculateSimpleMaxPriceWithIncome() {
    const { simpleDownPayment, simpleInterestRate } = this.state;

    const maxMonthlyPayment = this.calculateSimpleMaximumMonthlyPayment();
    const mortgagePayment = maxMonthlyPayment * 0.75;

    const c = (parseFloat(simpleInterestRate) / 100) / 12;
    const mortgageMax = Math.ceil(mortgagePayment / ((c * Math.pow((1+c), 360)) / (Math.pow((1+c), 360) - 1)));

    return mortgageMax + parseInt(simpleDownPayment, 10);
  }

  calculateSimpleCannotAffordDefaultHouseBudget() {
    const { simpleDownPayment } = this.state;

    const defaultDownPayment = Math.ceil(simpleDownPayment / 0.20);
    const maxPriceForIncome = this.calculateSimpleMaxPriceWithIncome();

    return (defaultDownPayment > maxPriceForIncome);
  }

  calculateSimpleHouseBudget(cannotAffordDefault = false, downPaymentPercentage = null) {
    const { simpleDownPayment, simpleDownPaymentPercent } = this.state;

    if (downPaymentPercentage === null) {
      downPaymentPercentage = (simpleDownPaymentPercent === 0) ? 20 : simpleDownPaymentPercent;
    }

    let budget = 0;
    if (cannotAffordDefault) {
      budget = this.calculateSimpleMaxPriceWithIncome()
    } else {
      budget = Math.ceil(simpleDownPayment / (parseInt(downPaymentPercentage, 10) / 100))
    }

    return (budget < 0) ? 0 : budget;
  }

  calculateSimpleMaximumMonthlyPayment() {
    const { simpleAnnualHouseholdIncome, simpleMonthlyDebtPayments } = this.state;

    const grossMonthlyIncome = simpleAnnualHouseholdIncome / 12;
    const threshold = grossMonthlyIncome * 0.36;

    return Math.ceil(threshold - simpleMonthlyDebtPayments);
  }

  calculateSimpleMonthlyPayment(cannotAffordDefault = false, downPaymentPercentage = null) {
    const { simpleDownPayment, simpleDownPaymentPercent, simpleInterestRate } = this.state;

    if (downPaymentPercentage === null) {
      downPaymentPercentage = (simpleDownPaymentPercent === 0) ? 20 : simpleDownPaymentPercent;
    }

    let payment = 0;
    if (cannotAffordDefault) {
      payment = this.calculateSimpleMaximumMonthlyPayment()
    } else {
      const maxPrice = this.calculateSimpleHouseBudget(cannotAffordDefault, downPaymentPercentage);

      const loan = maxPrice - parseInt(simpleDownPayment, 10);
      const c = (parseFloat(simpleInterestRate) / 100) / 12;

      const mortgage = Math.floor((loan * c) / (1 - Math.pow(1 + c, -360)));
      const pmi = (parseInt(downPaymentPercentage, 10) < 20) ? Math.floor((loan * 0.01) / 12) : 0;
      const propertyTax = Math.floor((maxPrice * 0.01) / 12);
      const insurance = Math.floor((maxPrice * 0.0075) / 12);

      payment = mortgage + pmi + propertyTax + insurance;
    }

    return (payment < 0) ? 0 : payment;
  }

  calculateSimpleDownPaymentPercent(cannotAffordDefault = false) {
    const { simpleDownPayment } = this.state;

    if (cannotAffordDefault) {
      const maxPriceForIncome = this.calculateSimpleMaxPriceWithIncome();
      const percent = Math.ceil((simpleDownPayment / maxPriceForIncome) * 100);

      return (percent < 0 || percent > 99) ? 20 : percent;
    } else {
      return 20;
    }
  }

  calculateSimpleMinViableDownPaymentPercent() {
    const maxMonthlyPayment = this.calculateSimpleMaximumMonthlyPayment();

    for (let p = 19; p > 0; p--) {
      let monthlyPayment = this.calculateSimpleMonthlyPayment(false, p);

      if (monthlyPayment > maxMonthlyPayment) {
        return p + 1;
      }
    }

    return 1;
  }

  simpleMonthlyPaymentBreakdown(cannotAffordDefault=false) {
    const { simpleDownPayment, simpleDownPaymentPercent, simpleInterestRate } = this.state;

    if (cannotAffordDefault) {
      return '<p>Your monthly debt payments + your estimated housing budget added up to more than 36% of your monthly income, so your monthly payment was adjusted downwards to meet this limit.</p>';
    } else {
      let mortgage = 0;
      let pmi = 0;
      let propertyTax = 0;
      let insurance = 0;
      let total = 0;

      if (simpleDownPayment !== 0 && simpleDownPayment !== null && simpleInterestRate !== null) {
        const downPaymentPercent = (simpleDownPaymentPercent === 0) ? 20 : simpleDownPaymentPercent;
        const maxPrice = this.calculateSimpleHouseBudget(cannotAffordDefault, downPaymentPercent);
        const loan = maxPrice - parseInt(simpleDownPayment, 10);
        const c = (parseFloat(simpleInterestRate) / 100) / 12;

        mortgage = Math.floor((loan * c) / (1 - Math.pow(1 + c, -360)));
        pmi = (parseInt(downPaymentPercent, 10) < 20) ? Math.floor((loan * 0.01) / 12) : 0;
        propertyTax = Math.floor((maxPrice * 0.01) / 12);
        insurance = Math.floor((maxPrice * 0.0075) / 12);
        total = mortgage+pmi+propertyTax+insurance;
      }

      // react-boostrap Popover requires inline-styles to customize styles
      const titleStyle = 'font-weight: bold; width: 50%; display: inline-block; color: #450295;';
      const dollarStyle = 'font-weight: bold; width: 50%; display: inline-block; text-align: right; color: #450295;';
      const totalTitleStyle = 'font-weight: bold; width: 50%; display: inline-block; color: #1fa1ff; border-top: 1px solid #535353;';
      const totalDollarStyle = 'font-weight: bold; width: 50%; display: inline-block; text-align: right; color: #1fa1ff; border-top: 1px solid #535353; padding-bottom: 1rem;';

      const mortgageDiv = '<div><div style="'+titleStyle+'">Mortgage</div><div style="'+dollarStyle+'">'+formatCurrency(mortgage)+'</div></div>';
      const pmiDiv = '<div><div style="'+titleStyle+'">PMI</div><div style="'+dollarStyle+'">'+formatCurrency(pmi)+'</div></div>';
      const propertyTaxDiv = '<div><div style="'+titleStyle+'">Property Tax</div><div style="'+dollarStyle+'">'+formatCurrency(propertyTax)+'</div></div>';
      const insuranceDiv = '<div><div style="'+titleStyle+'">Insurance</div><div style="'+dollarStyle+'">'+formatCurrency(insurance)+'</div></div>';
      const totalDiv = '<div><div style="'+totalTitleStyle+'">Total</div><div style="'+totalDollarStyle+'">'+formatCurrency(total)+'</div></div>';
      const informationText = '<p>In addition to paying your mortgage each month, you’ll be responsible for property tax and homeowners insurance. The tax and insurance values listed in this breakdown have been calculated from national averages. This breakdown will change depending on the home you can afford and the down payment you have saved.</p>';

      return mortgageDiv+pmiDiv+propertyTaxDiv+insuranceDiv+totalDiv+informationText;
    }
  }

  render () {
    const {
      detailedView,
      grossMonthlyIncome,
      creditCardDebt,
      studentDebt,
      autoLoan,
      otherDebt,
      propertyTax,
      insurance,
      maintenance,
      interestRate,
      downPayment,
      simpleAnnualHouseholdIncome,
      simpleMonthlyDebtPayments,
      simpleDownPayment,
      simpleDownPaymentPercent,
      simpleInterestRate
    } = this.state;

    // Calculated Fields for Detailed Calculator
    const calculatedGrossMonthlyIncome = this.calculateGrossMonthlyIncome();
    const calculatedEstimatedHousingBudget = this.calculateEstimatedHousingBudget(calculatedGrossMonthlyIncome);
    const calculatedTotalMonthlyDebtPayments = this.calculateTotalMonthlyDebtPayments();
    const calculatedAdjustedMonthlyHousingBudget = this.calculateAdjustedMonthlyHousingBudget(calculatedGrossMonthlyIncome);
    const calculatedPropertyTax = this.calculatePropertyTax(calculatedGrossMonthlyIncome);
    const calculatedInsurance = this.calculateInsurance(calculatedGrossMonthlyIncome);
    const calculatedMaintenance = this.calculateMaintenance(calculatedGrossMonthlyIncome);
    const calculatedTotalOngoingHousingExpenses = this.calculateTotalOngoingHousingExpenses(calculatedGrossMonthlyIncome);
    const calculatedEstimatedMaxMortgagePayment = this.calculateEstimatedMaxMortgagePayment(calculatedGrossMonthlyIncome);
    const calculatedEstimatedMaxMortgageAmount = this.calculateEstimatedMaxMortgageAmount(calculatedGrossMonthlyIncome);
    const calculatedDownPayment = this.calculateDownPayment(calculatedGrossMonthlyIncome);
    const calculatedMaxPrice = this.calculateMaxPrice(calculatedGrossMonthlyIncome);

    // Calculated Fields for Fast & Simple Calculator
    const cannotAffordDefault = this.calculateSimpleCannotAffordDefaultHouseBudget();
    const calculatedSimpleHouseBudget = this.calculateSimpleHouseBudget(cannotAffordDefault);
    const calculatedSimpleMonthlyPayments = this.calculateSimpleMonthlyPayment(cannotAffordDefault);
    const calculatedSimpleDownPaymentPercent = this.calculateSimpleDownPaymentPercent(cannotAffordDefault);
    const calculatedMinViableDownPaymentPercent = this.calculateSimpleMinViableDownPaymentPercent();
    const simpleMonthlyPaymentBreakdown = this.simpleMonthlyPaymentBreakdown(cannotAffordDefault);

    return (
      <div>
      {detailedView ? (
          <Calculator title="Detailed Calculator">
            <div className="calculator-body">
              <Form>
                <CurrencyField
                  name="grossMonthlyIncome"
                  label="Gross monthly income"
                  value={grossMonthlyIncome}
                  popover={popoverMessages.grossMonthlyIncome}
                  calculatedValue={calculatedGrossMonthlyIncome}
                  onChange={(e, value) => this.handleChange(value, 'grossMonthlyIncome') }
                  validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onValidation}
                />

                <CurrencyField
                  name="estimatedHousingBudget"
                  label="Estimated monthly housing budget"
                  value={calculatedEstimatedHousingBudget}
                  calculatedValue={calculatedEstimatedHousingBudget}
                  onChange={(e, value) => this.handleChange(value, 'estimatedHousingBudget') }
                  popover={popoverMessages.estimatedHousingBudget}
                  disabled={true}
                />

                <CalculatorGroup label="Monthly debt payments">
                  <CurrencyField
                    name="creditCardDebt"
                    label="Credit card debt"
                    value={creditCardDebt}
                    onChange={(e, value) => this.handleChange(value, 'creditCardDebt') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />

                  <CurrencyField
                    name="studentDebt"
                    label="Student debt"
                    value={studentDebt}
                    onChange={(e, value) => this.handleChange(value, 'studentDebt') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />

                  <CurrencyField
                    name="autoLoan"
                    label="Auto loan/lease"
                    value={autoLoan}
                    onChange={(e, value) => this.handleChange(value, 'autoLoan') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />

                  <CurrencyField
                    name="otherDebt"
                    label="Other"
                    value={otherDebt}
                    onChange={(e, value) => this.handleChange(value, 'otherDebt') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />
                </CalculatorGroup>

                <CurrencyField
                  name="totalMonthlyDebtPayments"
                  label="Total monthly debt payments"
                  value={calculatedTotalMonthlyDebtPayments}
                  calculatedValue={calculatedTotalMonthlyDebtPayments}
                  onChange={(e, value) => this.handleChange(value, 'totalMonthlyDebtPayments') }
                  disabled={true}
                />

                <CurrencyField
                  name="adjustedMonthlyHousingBudget"
                  label="Adjusted monthly housing budget"
                  value={calculatedAdjustedMonthlyHousingBudget}
                  calculatedValue={calculatedAdjustedMonthlyHousingBudget}
                  onChange={(e, value) => this.handleChange(value, 'adjustedMonthlyHousingBudget') }
                  popover={popoverMessages.adjustedMonthlyHousingBudget}
                  disabled={true}
                />

                <CalculatorGroup
                  label="Ongoing housing expenses"
                  subLabel="Your mortgage isn’t the only housing expense you need to plan for. There are other unavoidable costs you must factor into your calculation such as property tax, insurance and maintenance. These can be estimated as percentages of your Adjusted Monthly Housing Budget and the calculator will put them in automatically. If you know the exact numbers you can enter them below."
                  popover={popoverMessages.ongoingHousingExpenses}
                >
                  <CurrencyField
                    name="propertyTax"
                    label="Property Tax"
                    value={propertyTax}
                    calculatedValue={calculatedPropertyTax}
                    onChange={(e, value) => this.handleChange(value, 'propertyTax') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />

                  <CurrencyField
                    name="insurance"
                    label="Insurance"
                    value={insurance}
                    calculatedValue={calculatedInsurance}
                    onChange={(e, value) => this.handleChange(value, 'insurance') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />

                  <CurrencyField
                    name="maintenance"
                    label="Maintenance"
                    value={maintenance}
                    calculatedValue={calculatedMaintenance}
                    onChange={(e, value) => this.handleChange(value, 'maintenance') }
                    validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                    onValidation={this.onValidation}
                  />

                  <CurrencyField
                    name="totalOngoingHousingExpenses"
                    label="Total"
                    value={calculatedTotalOngoingHousingExpenses}
                    calculatedValue={calculatedTotalOngoingHousingExpenses}
                    onChange={(e, value) => this.handleChange(value, 'totalOngoingHousingExpenses') }
                    disabled={true}
                  />
                </CalculatorGroup>

                <CurrencyField
                  name="estimatedMaxMortgagePayment"
                  label="Estimated maximum mortgage payment"
                  value={calculatedEstimatedMaxMortgagePayment}
                  calculatedValue={calculatedEstimatedMaxMortgagePayment}
                  onChange={(e, value) => this.handleChange(value, 'estimatedMaxMortgagePayment') }
                  disabled={true}
                />

                <PercentageField
                  name="interestRate"
                  label="Interest rate"
                  value={interestRate}
                  popover={popoverMessages.interestRate}
                  onChange={(e, value) => this.handleChange(value, 'interestRate') }
                  validator={minMaxValidator(0, 99.99, formatPercentage)}
                  onValidation={this.onValidation}
                />

                <CurrencyField
                  name="estimatedMaxMortgageAmount"
                  label="Estimated maximum mortgage amount"
                  value={calculatedEstimatedMaxMortgageAmount}
                  calculatedValue={calculatedEstimatedMaxMortgageAmount}
                  onChange={(e, value) => this.handleChange(value, 'estimatedMaxMortgageAmount') }
                  disabled={true}
                />

                <InformationBlock
                  text="The calculations here assume you have a 20% down payment. If you do not put down 20%, you will have to pay Private Mortgage Insurance (PMI) which typically costs about 1% of the amount borrowed every year. On a mortgage of $200,000, for example, PMI would be about $167/month (1% of $200,000 / 12)."
                />

                <CurrencyField
                  name="downPayment"
                  label="20% down payment"
                  value={downPayment}
                  calculatedValue={calculatedDownPayment}
                  onChange={(e, value) => this.handleChange(value, 'downPayment') }
                  validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onValidation}
                />
              </Form>
              <CalculatorResults>
                <CalculatorResult
                  label={'Estimated Maximum Price You Can Afford'}
                  value={formatCurrency(Math.ceil(calculatedMaxPrice))}
                />
              </CalculatorResults>
              <Row>
                <Col xs={12} className="calculator-buttons">
                  <Button bsStyle="calculator-clear" onClick={this.clearState}>Clear All</Button>
                  <Button bsStyle="primary" onClick={this.toggleCalculator}>Simple Calculator</Button>
                </Col>
              </Row>
            </div>
          </Calculator>
        ) : (
          <Calculator title="Fast & Simple Calculator">
            <div className="calculator-body">
              <Form>
                <CurrencyField
                  name="simpleAnnualHouseholdIncome"
                  label="Annual household income"
                  value={simpleAnnualHouseholdIncome}
                  onChange={(e, value) => this.handleChange(value, 'simpleAnnualHouseholdIncome') }
                  validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onValidation}
                  popover={popoverMessages.simpleAnnualHouseholdIncome}
                />

                <CurrencyField
                  name="simpleMonthlyDebtPayments"
                  label="Monthly debt payments"
                  value={simpleMonthlyDebtPayments}
                  onChange={(e, value) => this.handleChange(value, 'simpleMonthlyDebtPayments') }
                  validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onValidation}
                  popover={popoverMessages.simpleMonthlyDebtPayments}
                />

                <CurrencyField
                  name="simpleDownPayment"
                  label="Down payment"
                  value={simpleDownPayment}
                  onChange={(e, value) => this.handleChange(value, 'simpleDownPayment') }
                  validator={minMaxValidator(0, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onValidation}
                  popover={popoverMessages.simpleDownPayment}
                />

                <PercentageField
                  name="simpleInterestRate"
                  label="Interest rate"
                  value={simpleInterestRate}
                  popover={popoverMessages.interestRate}
                  onChange={(e, value) => this.handleChange(value, 'simpleInterestRate') }
                  validator={minMaxValidator(0, 99.99, formatPercentage)}
                  onValidation={this.onValidation}
                />

                <SimpleSliderField
                  name="simpleDownPaymentPercent"
                  label="Down payment %"
                  subLabel="We automatically assume the down payment you have saved will be worth 20% of the home you can afford. (That way you can avoid paying private mortgage insurance). Use the slider if you’d like to pay less than 20% down and we’ll estimate how that might affect what you can afford."
                  value={(calculatedSimpleDownPaymentPercent > 20 || simpleDownPaymentPercent === 0) ? calculatedSimpleDownPaymentPercent : simpleDownPaymentPercent}
                  onChange={(e, value) => this.handleChange(value, 'simpleDownPaymentPercent') }
                  aria-labelledby="continuous-slider"
                  valueLabelDisplay="on"
                  min={calculatedMinViableDownPaymentPercent}
                  max={(cannotAffordDefault) ? calculatedSimpleDownPaymentPercent : 20}
                  disabled={cannotAffordDefault || calculatedMinViableDownPaymentPercent === 20}
                />
              </Form>
              <CalculatorResults>
                <SimpleCalculatorResult
                  label={'Monthly payments'}
                  value={formatCurrency(calculatedSimpleMonthlyPayments)}
                  popover={simpleMonthlyPaymentBreakdown}
                  helpText={'See breakdown'}
                />
                <SimpleCalculatorResult
                  label={'You can afford a house up to'}
                  value={formatCurrency(calculatedSimpleHouseBudget)}
                />
              </CalculatorResults>
              <Row>
                <Col xs={12} className="calculator-buttons">
                  <Button bsStyle="calculator-clear" onClick={this.clearState}>Clear All</Button>
                  <Button bsStyle="primary" onClick={this.toggleCalculator}>Detailed Calculator</Button>
                </Col>
              </Row>
            </div>
          </Calculator>
        )}
      </div>
    )
  }
}

embedComponent(AffordHomeBudgetCalculator, 'tgm-afford-home-budget-calc', 'tgm-afford-home-budget-calc-embed')

export default AffordHomeBudgetCalculator



// WEBPACK FOOTER //
// ./src/calculators/AffordHomeBudgetCalculator.js